// NOTE: This is part of the lower level of the New Computation Model, and
// should be kept isolated from non-trivial Glide dependencies.  In
// particular, it shouldn't have to know anything about Glide table/column
// types/schemas.

import sortBy from "lodash/sortBy";
import { assert, assertNever, DefaultMap, defined } from "@glideapps/ts-necessities";

import { follow, GroundValue } from "./data";
import { Dirt, Handler, MutableNamespace, OnChangeCallback } from "./types";
import { RootPath, makeRootPath, isTopLevelPath } from "./paths";
import { makeRowID } from "../make-row-id";
import { logError, logInfo } from "../debug-print";

function jsonifyDirt(d: Dirt): unknown {
    if (d.kind === "row") {
        return {
            kind: "row",
            rowID: d.rowID,
            columns: d.columns === true ? true : Array.from(d.columns),
        };
    } else if (d.kind === "table") {
        return {
            kind: "table",
            columns: d.columns === true ? true : Array.from(d.columns),
        };
    } else {
        return assertNever(d);
    }
}

interface TimingResult<TMetadata> {
    readonly metadata: TMetadata | undefined;
    readonly name: string;
    readonly totalTime: number;
}

interface Entity<TMetadata> {
    // Identifies the entity - the first item in the root path
    readonly key: string;
    readonly handler: Handler;
    // Set of keys
    readonly dependents: Set<string>;
    // FIXME: Maybe this should live in the handler, so we can
    // look at that locally to keep consistency?
    hasDirtyDependencies: boolean;
    callbacksNotified: boolean;

    // For debugging
    readonly name: string;
    totalTime: number;
    readonly metadata: TMetadata | undefined;
}

export interface NamespaceGraphNode {
    readonly id: number;
    readonly label: string;
    readonly level: number;
    readonly key: string;
}

export interface NamespaceGraphEdge {
    readonly id: number;
    readonly from: number;
    readonly to: number;
    readonly arrows: string;
}

export interface NamespaceGraph {
    readonly nodes: NamespaceGraphNode[];
    readonly edges: NamespaceGraphEdge[];
}

// The namespace is only concerned with "indirectly" dirty entities, which is
// entities whose dependencies have received dirt but not yet passed on.
// For those, we need to first do a depth-first pass over their dependencies
// get all the dirt to percolate up.
export class NamespaceImpl<TMetadata> implements MutableNamespace<TMetadata> {
    private _entitiesByKey = new Map<string, Entity<TMetadata>>();
    private _entitiesByHandler = new Map<Handler, Entity<TMetadata>>();
    private _numPushes = 0;
    private _numProcesses = 0;
    private _pushesFromTo = new DefaultMap<string, DefaultMap<string, number>>(() => new DefaultMap(() => 0));

    // [keys, callback]
    private _subscribers: [ReadonlySet<string>, OnChangeCallback][] = [];
    private _isStrictMode = false;

    constructor(private readonly _printDirt: boolean) {}

    private getMaybeEntity(path: string | RootPath): Entity<TMetadata> | undefined {
        if (typeof path !== "string") {
            path = path.rest.key;
        }
        return this._entitiesByKey.get(path);
    }

    private getEntity(path: string | RootPath): Entity<TMetadata> {
        return defined(this.getMaybeEntity(path));
    }

    private assertConnections(): void {
        if (process.env.NODE_ENV === "production") return;

        for (const [key, entity] of this._entitiesByKey) {
            for (const slot of entity.handler.getSlots()) {
                const e = this.getEntity(slot.sourcePath);
                assert(e.dependents.has(key));
            }

            for (const k of entity.dependents) {
                const e = this.getEntity(k);
                let found = false;
                for (const slot of e.handler.getSlots()) {
                    if (key === slot.sourcePath.rest.key) {
                        found = true;
                        break;
                    }
                }
                assert(found);
            }
        }
    }

    public get isStrictMode(): boolean {
        return this._isStrictMode;
    }

    public setStrictMode(isStrictMode: boolean) {
        this._isStrictMode = isStrictMode;
    }

    public get(path: RootPath, assertNotDirty: boolean = false): GroundValue {
        const entity = this.getMaybeEntity(path);
        if (entity === undefined) {
            logError("No entity for path", path);
            return undefined;
        }
        // logInfo(
        //     "getting",
        //     entity.key,
        //     entity.name,
        //     entity.handler.constructor.name,
        //     "dirty dependencies",
        //     entity.hasDirtyDependencies
        // );
        if (entity.hasDirtyDependencies) {
            assert(!assertNotDirty);
            const slots = entity.handler.getSlots();
            // logInfo("getting slots", entity.key, slots.length);
            for (const slot of slots) {
                this.get(slot.sourcePath, false);
            }

            // logInfo("undirtying dependencies", entity.key);
            entity.hasDirtyDependencies = false;
            for (const slot of slots) {
                assert(!this.getEntity(slot.sourcePath).hasDirtyDependencies);
            }
        } else {
            if (process.env.NODE_ENV !== "production") {
                for (const slot of entity.handler.getSlots()) {
                    const e = this.getEntity(slot.sourcePath);
                    assert(!e.hasDirtyDependencies);
                    assert(!e.handler.isDirty);
                }
            }
        }

        // logInfo("recomputing", entity.key);
        const start = Date.now();
        const result = entity.handler.recompute(this);
        assert(!entity.hasDirtyDependencies);
        entity.callbacksNotified = false;
        entity.totalTime += Date.now() - start;

        if (path.rest.rest === undefined) {
            return result;
        } else {
            return follow(result, path.rest.rest);
        }
    }

    private markDependentsDirty(entity: Entity<TMetadata>): void {
        this.notifySubscribers(entity);
        for (const key of entity.dependents) {
            const e = this.getEntity(key);
            if (e.hasDirtyDependencies) {
                assert(e.callbacksNotified);
                continue;
            }
            // logInfo("marking dependency dirty", entity.key, "->", key);
            e.hasDirtyDependencies = true;
            e.handler.dependenciesAreDirty?.();
            this.markDependentsDirty(e);
        }
    }

    public handlerWasDirtied(handler: Handler): void {
        const entity = defined(this._entitiesByHandler.get(handler));
        assert(handler.isDirty);
        this.markDependentsDirty(entity);
    }

    public pushDirt(handler: Handler, dirt: Dirt): void {
        const entity = defined(this._entitiesByHandler.get(handler));

        this._numPushes++;

        // FIXME: Shouldn't this run in a `setTimeout`?
        this.notifySubscribers(entity);

        for (const dependent of entity.dependents) {
            const e = this.getEntity(dependent);
            let propagate = false;
            for (const q of e.handler.getSlots()) {
                if (q.sourcePath.rest.key === entity.key) {
                    this._numProcesses++;
                    this._pushesFromTo.get(entity.name).update(e.name, x => x + 1);

                    if (this._printDirt) {
                        logInfo(`  ${entity.name} -> ${e.name}: ${JSON.stringify(jsonifyDirt(dirt))}`);
                    }
                    if (q.process(dirt)) {
                        propagate = true;
                    }
                }
            }
            if (propagate) {
                this.markDependentsDirty(e);
            }
        }
    }

    public addEntity(name: string, handler: Handler, metadata: TMetadata | undefined): RootPath {
        const key = makeRowID();
        const entity: Entity<TMetadata> = {
            key,
            name,
            handler,
            dependents: new Set(),
            hasDirtyDependencies: true,
            callbacksNotified: true,
            totalTime: 0,
            metadata,
        };
        this._entitiesByKey.set(key, entity);
        assert(!this._entitiesByHandler.has(handler));
        this._entitiesByHandler.set(handler, entity);

        for (const q of handler.getSlots()) {
            const e = this.getEntity(q.sourcePath);
            e.dependents.add(key);
        }

        handler.setDirty();
        handler.connect?.(this);

        this.assertConnections();

        return makeRootPath(key);
    }

    public deleteEntity(path: RootPath): void {
        assert(isTopLevelPath(path));
        const {
            rest: { key },
        } = path;

        const entity = this.getEntity(key);

        entity.handler.disconnect?.(this);

        assert(this._entitiesByKey.has(key));
        this._entitiesByKey.delete(key);
        assert(this._entitiesByHandler.has(entity.handler));
        this._entitiesByHandler.delete(entity.handler);

        const dependencies: Entity<TMetadata>[] = [];
        for (const q of entity.handler.getSlots()) {
            const e = this.getEntity(q.sourcePath);
            assert(e.dependents.has(key));
            dependencies.push(e);
        }
        for (const e of dependencies) {
            e.dependents.delete(key);
        }

        this.assertConnections();
    }

    public hasHandler(handler: Handler): boolean {
        return this._entitiesByHandler.has(handler);
    }

    public getAndResetCounters(): [number, number, Map<string, Map<string, number>>] {
        const numPushes = this._numPushes;
        const numProcesses = this._numProcesses;
        const pushesFromTo = this._pushesFromTo;
        this._numPushes = 0;
        this._numProcesses = 0;
        this._pushesFromTo = new DefaultMap(() => new DefaultMap(() => 0));
        return [numPushes, numProcesses, pushesFromTo];
    }

    public makeGraph(): NamespaceGraph {
        let nextNodeID = 1;
        const nodeIDForKey = new DefaultMap<string, number>(() => nextNodeID++);

        let nextEdgeID = 1;

        const nodes: NamespaceGraphNode[] = [];
        const edges: NamespaceGraphEdge[] = [];

        const inDegree = new DefaultMap<string, number>(() => 0);

        for (const key of this._entitiesByKey.keys()) {
            inDegree.set(key, 0);
        }
        for (const entity of this._entitiesByKey.values()) {
            for (const depKey of entity.dependents) {
                inDegree.update(depKey, x => x + 1);
            }
        }

        const level = new Map<string, number>();
        let nextLevel = 0;
        while (inDegree.size > 0) {
            const zeroes = Array.from(inDegree.entries())
                .filter(([, d]) => d === 0)
                .map(([k]) => k);
            assert(zeroes.length > 0);

            for (const k of zeroes) {
                inDegree.delete(k);
                const entity = defined(this._entitiesByKey.get(k));
                for (const depKey of entity.dependents) {
                    inDegree.update(depKey, x => x - 1);
                }
                level.set(k, nextLevel);
            }

            nextLevel++;
        }

        for (const [key, entity] of this._entitiesByKey) {
            const id = nodeIDForKey.get(key);
            nodes.push({ id, label: `${entity.name}\n${entity.totalTime}`, level: defined(level.get(key)), key });
            for (const depKey of entity.dependents) {
                edges.push({ id: nextEdgeID++, from: id, to: nodeIDForKey.get(depKey), arrows: "to" });
            }
        }

        return { nodes, edges };
    }

    public debugPrintNode({ label, key }: NamespaceGraphNode): void {
        logInfo("Node", label, this.getEntity(key));
    }

    public makeGraphviz(): string {
        const { nodes, edges } = this.makeGraph();

        const lines: string[] = [];
        function nameForID(id: number) {
            return `node${id}`;
        }

        lines.push("digraph D {");
        lines.push("    rankdir=LR;");

        for (const { id, label } of nodes) {
            lines.push(`    ${nameForID(id)} [label="${label}"];`);
        }

        for (const { from, to } of edges) {
            lines.push(`    ${nameForID(from)} -> ${nameForID(to)};`);
        }

        lines.push("}");

        return lines.join("\n");
    }

    public setAllDirty(): void {
        for (const entity of this._entitiesByKey.values()) {
            entity.handler.setDirty();
            entity.hasDirtyDependencies = true;
            entity.handler.dependenciesAreDirty?.();
            this.notifySubscribers(entity);
        }
    }

    public resetTiming(print: boolean = false): void {
        const entities = sortBy(Array.from(this._entitiesByKey.values()), e => -e.totalTime);
        if (print) {
            logInfo("*** Reset Timing ***");
        }
        for (const e of entities) {
            if (print) {
                logInfo("   ", e.name, e.totalTime);
            }
            e.totalTime = 0;
        }
    }

    public getTiming(): readonly TimingResult<TMetadata>[] {
        const entries: TimingResult<TMetadata>[] = [];
        for (const e of this._entitiesByKey.values()) {
            if (e.totalTime > 0) {
                entries.push({ metadata: e.metadata, name: e.name, totalTime: e.totalTime });
            }
        }
        return entries;
    }

    public checkConsistency(): void {
        assert(this._entitiesByKey.size === this._entitiesByHandler.size);

        for (const [key, entity] of this._entitiesByKey.entries()) {
            assert(this._entitiesByHandler.get(entity.handler) === entity);

            const slots = entity.handler.getSlots();

            for (const slot of slots) {
                const e = this.getEntity(slot.sourcePath);
                assert(e.dependents.has(key));

                if (!entity.hasDirtyDependencies) {
                    assert(!e.hasDirtyDependencies);
                    assert(!e.handler.isDirty);
                }
            }

            for (const k of entity.dependents) {
                const e = defined(this._entitiesByKey.get(k));
                assert(e.handler.getSlots().some(s => this.getEntity(s.sourcePath) === entity));
            }

            for (const slot of slots) {
                const e = this.getEntity(slot.sourcePath);
                if (e.hasDirtyDependencies) {
                    assert(entity.hasDirtyDependencies);
                }
            }
        }
    }

    // When do subscribers have to fire?
    //
    // * When dirt is pushed from the subscribed path.
    // * When we add dirt to the subscribed path.
    // * When we mark the path as `hasDirtyDependencies`.
    //
    // Doing both will lead to subscribers firing more than once.  Let's say C
    // depends on B, depends on A, and there's a subscriber on C. A is updated
    // somehow and pushes dirt to B.  Then we mark B's dependents as dirty,
    // i.e. C.  This fires the callback.  Now we recompute C, which recomputed
    // B, which pushes dirt from B to C, which fires the callback again.  If C
    // also pushes dirt, it'll fire the callback a third time.
    //
    // We might be able to solve this by keeping track of whether we've fired
    // for a specific entity, and not firing again until it's fully
    // recomputed.
    private notifySubscribers(entity: Entity<TMetadata>): void {
        const { callbacksNotified, key } = entity;

        if (callbacksNotified) return;
        entity.callbacksNotified = true;

        for (const [keys, onChange] of this._subscribers) {
            if (keys.has(key)) {
                onChange();
            }
        }
    }

    public addSubscriber(paths: readonly RootPath[], onChange: OnChangeCallback): void {
        assert(!this._subscribers.some(([, c]) => c === onChange));

        const keys = new Set(paths.map(p => p.rest.key));
        this._subscribers.push([keys, onChange]);
    }

    public removeSubscriber(onChange: OnChangeCallback): void {
        let numFound = 0;
        this._subscribers = this._subscribers.filter(([, c]) => {
            if (c !== onChange) return true;
            numFound++;
            return false;
        });
        assert(numFound === 1);
    }

    public retire(): void {
        for (const e of this._entitiesByKey.values()) {
            e.handler.disconnect?.(this);
        }
    }

    public debugPrintGraph(): void {
        for (const entity of this._entitiesByKey.values()) {
            const flags: string[] = [];
            if (entity.hasDirtyDependencies) {
                flags.push("dirtyDeps");
            }
            if (entity.callbacksNotified) {
                flags.push("notified");
            }
            if (this._subscribers.some(([ks]) => ks.has(entity.key))) {
                flags.push("subscr");
            }
            logInfo(`${entity.key} ${entity.name} ${flags.join(",")}`);
            const slots = entity.handler.getSlots();
            if (slots.length > 0) {
                logInfo(`  <- ${slots.map(s => s.sourcePath.rest.key).join(" ")}`);
            }
            if (entity.dependents.size > 0) {
                logInfo(`  -> ${Array.from(entity.dependents).join(" ")}`);
            }
        }
    }
}

export class PathsSubscriber<TMetadata> {
    private _subscribed: boolean = false;

    constructor(private readonly _ns: NamespaceImpl<TMetadata>, private readonly _onChange: OnChangeCallback) {}

    public setPaths(paths: readonly RootPath[]) {
        if (this._subscribed) {
            this._ns.removeSubscriber(this._onChange);
            this._subscribed = false;
        }

        if (paths.length === 0) return;

        this._ns.addSubscriber(paths, this._onChange);
        this._subscribed = true;
    }

    public retire() {
        this.setPaths([]);
    }
}
